/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { getInboundShipmentForInvoice } from "../../../graphql/inbound/custom-queries";
import { getOutboundShipmentForInvoice } from "../../../graphql/outbound/custom-queries";

import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { Grid, InputAdornment } from "@material-ui/core";
import { AccountDropDown } from "../../../components/common/AccountDropDown";

import {
  Create,
  FileInput,
  FormDataConsumer,
  FormTab,
  Loading,
  required,
  SelectInput,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { AttachFileIcon, CalendarIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { FileField } from "../../../components/common/FileField";
import { CustomFields } from "../../../components/common/CustomFields";
import { ACCEPTED_FILES, MAX_FILES } from "../../../utils/constants";
import { handleFileChange } from "../../../utils/fileUtils";
import { getBackPath } from "../../../utils/utils";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
  hasExternalId,
} from "../../../utils/zapierUtils";

import { ChargeEvent } from "../accounts/chargeRules/chargeRuleConstants";
import {
  fetchInboundBillingRules,
  fetchOutboundBillingRules,
} from "../accounts/chargeRules/ChargeRuleUtils";
import {
  fetchAccountAndServices,
  getDueDate,
  getPaymentTerms,
  setupStorageInvoice,
  setupInboundInvoice,
  setupOutboundInvoice,
} from "./InvoiceUtils";
import { InvoiceCreateHelp } from "./InvoiceHelp";
import { ChargesTab } from "./ChargesTab";
import { ValidateCreateButton } from "./ValidateCreateButton";

const validateRequired = required();
const validateAccount = (value, allValues, isSyncEnabled) => {
  if (
    isSyncEnabled &&
    value &&
    allValues.account &&
    !hasExternalId(allValues.account)
  ) {
    return "Account not synced to an external system.";
  }
  return validateRequired(value, allValues);
};

const fetchInboundForInvoice = async (id) => {
  if (!id) return null;
  const res = await API.graphql(
    graphqlOperation(getInboundShipmentForInvoice, { id }),
  );
  return res?.data?.getInboundShipment ?? null;
};

const fetchOutboundForInvoice = async (id) => {
  if (!id) return null;
  const res = await API.graphql(
    graphqlOperation(getOutboundShipmentForInvoice, { id }),
  );
  return res?.data?.getOutboundShipment ?? null;
};

const InvoiceSetupTab = ({
  account,
  setAccount,
  setServices,
  selectedDueDate,
  setSelectedDueDate,
  initialValues,
  termList,
  isSyncEnabled,
  ...props
}) => {
  const form = useForm();
  const notify = useNotify();
  const isPreLoaded = initialValues.accountId;
  const [contacts, setContacts] = useState([]);

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "accountName",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );

  useEffect(() => {
    if (account) setContacts(account.billingContacts);
  }, [account]);

  useEffect(() => {
    if (contacts) {
      const contact = contacts.length > 0 ? contacts[0] : undefined;
      form.change("contact", contact);
    }
  }, [contacts]);

  const getAccountHelperText = (account) => {
    if (!account) return "";
    if (account.billingAddress) {
      account.address = account.billingAddress;
      return account?.address
        ? `${account.address.street}, ${account.address.city}, ${account.address.state} ${account.address.zipCode}`
        : null;
    }
    return "";
  };

  const handleAccountChange = async (value) => {
    const accountId = value?.id;
    if (accountId) {
      try {
        debugger;
        const { account, services } = await fetchAccountAndServices(accountId);
        account.services = { items: services };

        form.change("accountName", account.businessName);
        form.change("account", account);
        setAccount(account);
        const terms = getPaymentTerms(account.paymentTermId, termList);
        const dueDate = getDueDate(terms);
        form.change("dueDate", dueDate);
        setSelectedDueDate(dueDate);
        form.change("paymentTermId", terms?.id || "");
        form.change("paymentTerms", terms?.name || "");

        // serviceData = orderBy(serviceData, ["name"], ["asc"]);
        setServices(services);
      } catch (err) {
        form.change("accountId", "");
        logError("invoices:InvoiceCreate:handleAccountChange", err, notify);
      }
    }
  };

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "dueDate",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  const handleDueDateChange = (value) => {
    form.change("dueDate", value);
    setSelectedDueDate(value);
  };

  const handleTermsChange = (event) => {
    const value = event.target.value;
    const paymentTerm = getPaymentTerms(value);
    form.change("paymentTerms", paymentTerm?.name || "");

    const dueDate = getDueDate(value);
    form.change("dueDate", dueDate);
    setSelectedDueDate(dueDate);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="column" spacing={1}>
          <Grid container direction="row" spacing={1}>
            <Grid item xs={12} md={6}>
              {isPreLoaded ? (
                <TextInput
                  source="accountName"
                  label="Account"
                  variant="outlined"
                  fullWidth
                  disabled
                  helperText={getAccountHelperText(account)}
                />
              ) : (
                <AccountDropDown
                  source="accountId"
                  label="Account"
                  onAccountChange={handleAccountChange}
                  helperText={getAccountHelperText(account)}
                  validate={(value, allValues) =>
                    validateAccount(value, allValues, isSyncEnabled)
                  }
                  allowEmpty={false}
                  skipInvoice={true}
                  alertOverDue={true}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="contact"
                label="Point of Contact"
                variant="outlined"
                fullWidth
                disabled={!account}
                choices={contacts ? contacts : []}
                optionValue="name"
                optionText="name"
                allowEmpty
                resettable
                format={(v) => (!v ? "" : v.name)}
                parse={(v) => (!v ? "" : contacts.find((c) => c.name === v))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                source="poNumber"
                label="PO Number"
                variant="outlined"
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container direction="row" spacing={1}>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="paymentTermId"
                label="Payment Terms"
                choices={termList}
                onChange={handleTermsChange}
                allowEmpty
                fullWidth
                variant="outlined"
                helperText={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  label="Due Date"
                  margin="dense"
                  inputVariant="outlined"
                  variant="dialog"
                  fullWidth
                  disablePast
                  required
                  disabled={formData.paymentTermId}
                  error={!selectedDueDate}
                  helperText={selectedDueDate ? false : "Required"}
                  value={selectedDueDate ? selectedDueDate : null}
                  format="MM/dd/yyyy" // display format
                  onChange={handleDueDateChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="notes"
                label="Invoice Notes"
                variant="outlined"
                multiline
                minRows="5"
                fullWidth
              />
            </Grid>
            <CustomFields
              {...props}
              accountFields={account?.invoiceCustomFields}
              width={6}
              disabled={false}
            />
            <Grid item xs={12}>
              <FileInput
                source="attachments"
                label="ATTACHMENTS"
                variant="outlined"
                fullWidth
                placeholder={
                  <p>
                    <AttachFileIcon fontSize="large" /> <br /> Drop file(s) to
                    upload, or click to select.
                  </p>
                }
                accept={ACCEPTED_FILES}
                multiple
                maxFiles={MAX_FILES}
                options={{ maxFiles: MAX_FILES }}
                onChange={(value) =>
                  handleFileChange("attachments", value, form, notify)
                }
              >
                <FileField source="src" title="title" target="_blank" />
              </FileInput>
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const CreateActions = (props) => (
  <TopToolbar>
    <HelpButton>
      <InvoiceCreateHelp {...props} />
    </HelpButton>
    <BackButton {...props} />
  </TopToolbar>
);

const CreateToolbar = ({
  backPath,
  account,
  initialValues,
  isSyncEnabled,
  ...props
}) => {
  return (
    <Toolbar {...props}>
      <ValidateCreateButton
        account={account}
        backPath={backPath}
        initialValues={initialValues}
        isSyncEnabled={isSyncEnabled}
      />
    </Toolbar>
  );
};

const validateInvoiceCreate = (values) => {
  const errors = {};
  if (!values.dueDate) {
    errors.dueDate = "Required";
  }
  return errors;
};

export const InvoiceCreate = (props) => {
  const notify = useNotify();
  const isComponentMounted = useRef(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [account, setAccount] = useState(null);
  const [services, setServices] = useState([]);
  const backPath = getBackPath(props);
  const accountId = props.history?.location?.state?.accountId;
  const event = props.history?.location?.state?.event;
  const inboundShipmentId = props.history?.location?.state?.inboundShipmentId;
  const outboundShipmentId = props.history?.location?.state?.outboundShipmentId;

  const termList = window.appUser?.reloadYard?.paymentTerms?.items || [];
  const [isSyncEnabled, setIsSyncEnabled] = useState(
    getTriggerEnabled("invoiceCreated"),
  );

  const [initialValues, setInitialValues] = useState({
    active: "true",
    status: "NEW",
    dueDate: new Date(),
    subtotal: 0,
    discount: 0,
    adjustment: 0,
    lineItems: isSyncEnabled ? { items: [{ serviceId: "" }] } : undefined,
  });
  const [selectedDueDate, setSelectedDueDate] = useState(initialValues.dueDate);

  useEffect(() => {
    const checkSyncEnabled = async () => {
      const triggerEnabled = await fetchTriggerEnabled("invoiceCreated");
      setIsSyncEnabled(triggerEnabled);
      setInitialValues((prev) => {
        const hasLineItems = (prev?.lineItems?.items || []).length > 0;
        if (hasLineItems) return prev;
        return {
          ...prev,
          lineItems: triggerEnabled
            ? { items: [{ serviceId: "" }] }
            : undefined,
        };
      });
    };
    checkSyncEnabled();
  }, []);

  useEffect(() => {
    if (isComponentMounted) initInvoice();
    return () => (isComponentMounted.current = false);
  }, []);

  const initInvoice = async () => {
    let fetchedAccount = null;
    let fetchedServices = null;
    setLoading(true);
    debugger;

    try {
      if (accountId) {
        const { account, services } = await fetchAccountAndServices(accountId);
        account.services = { items: services };
        fetchedAccount = account;
        fetchedServices = services;
        setAccount(account);
        setServices(services);
      }
    } catch (err) {
      setError(true);
      logError("invoices:InvoiceCreate:getAccounts", err, notify);
    }

    if (fetchedAccount) {
      // Was this navigation launched from a shipment (preloaded path)?
      if (event === ChargeEvent.INBOUND && inboundShipmentId) {
        try {
          const inboundPromise = fetchInboundForInvoice(inboundShipmentId);
          const chargeRulesPromise = fetchInboundBillingRules(accountId);
          const [inbound, inboundRules] = await Promise.all([
            inboundPromise,
            chargeRulesPromise,
          ]);

          const invoiceData = setupInboundInvoice(
            fetchedAccount,
            inbound,
            inboundRules,
            fetchedServices,
          );

          setSelectedDueDate(invoiceData.dueDate);
          setInitialValues((prev) => ({ ...prev, ...invoiceData }));
        } catch (err) {
          setError(true);
          logError("invoices:InvoiceCreate:InvoiceFromInbound", err, notify);
        }
      } else if (event === ChargeEvent.OUTBOUND && outboundShipmentId) {
        try {
          const outboundPromise = fetchOutboundForInvoice(outboundShipmentId);
          const chargeRulesPromise = fetchOutboundBillingRules(accountId);
          const [outbound, outboundRules] = await Promise.all([
            outboundPromise,
            chargeRulesPromise,
          ]);

          const invoiceData = setupOutboundInvoice(
            fetchedAccount,
            outbound,
            outboundRules,
            fetchedServices,
          );

          setSelectedDueDate(invoiceData.dueDate);
          setInitialValues((prev) => ({ ...prev, ...invoiceData }));
        } catch (err) {
          setError(true);
          logError("invoices:InvoiceCreate:InvoiceFromOutbound", err, notify);
        }
      } else if (event === ChargeEvent.STORAGE) {
        try {
          const serviceDate = props.history.location.state.serviceDate;
          const dateRange = props.history.location.state.dateRange;
          const reportFile = props.history.location.state.reportFile;
          const billingData = props.history.location.state.billingData;

          const invoiceData = setupStorageInvoice(
            fetchedAccount,
            serviceDate,
            dateRange,
            reportFile,
            billingData,
          );

          setSelectedDueDate(invoiceData.dueDate);
          setInitialValues({ ...initialValues, ...invoiceData });
        } catch (err) {
          setError(true);
          logError("invoices:InvoiceCreate:StorageInvoice", err, notify);
        }
      }
    }
    setLoading(false);
  };

  if (loading) return <Loading />;
  return (
    <Create
      {...props}
      title={<ForceTitle title="New Invoice" />}
      actions={<CreateActions {...props} backPath={backPath} />}
    >
      <TabbedForm
        submitOnEnter={false}
        initialValues={initialValues}
        validate={validateInvoiceCreate}
        toolbar={
          <CreateToolbar
            {...props}
            backPath={backPath}
            account={account}
            initialValues={initialValues}
            isSyncEnabled={isSyncEnabled}
          />
        }
      >
        <FormTab path="" label="Invoice Setup">
          <InvoiceSetupTab
            account={account}
            setAccount={setAccount}
            setServices={setServices}
            selectedDueDate={selectedDueDate}
            setSelectedDueDate={setSelectedDueDate}
            initialValues={initialValues}
            termList={termList}
            isSyncEnabled={isSyncEnabled}
          />
        </FormTab>
        <FormTab path="charges" label="Charges">
          {!loading && !error && (
            <ChargesTab
              account={account}
              services={services}
              isSyncEnabled={isSyncEnabled}
              isLocked={false}
            />
          )}
        </FormTab>
      </TabbedForm>
    </Create>
  );
};
